import React from "react";
import { motion } from "framer-motion";
import { fadeUp, revealTransition, defaultViewport } from "./variants";

/**
 * Wraps a styled-component (or DOM tag) so it animates into view once,
 * when it scrolls into the viewport. Pass a styled-component via `as`
 * (e.g. `as={SectionTitle}`) to animate that element directly, no extra
 * wrapper node is introduced so layout (grid/flex) is unaffected.
 */
export function Reveal({
  as: Component = "div",
  variants = fadeUp,
  delay = 0,
  viewport = defaultViewport,
  transition,
  children,
  ...props
}) {
  const MotionComponent = React.useMemo(
    () => (typeof Component === "string" ? motion[Component] : motion(Component)),
    [Component]
  );

  return (
    <MotionComponent
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      variants={variants}
      transition={{ ...revealTransition, ...transition, delay }}
      {...props}
    >
      {children}
    </MotionComponent>
  );
}

/**
 * Container that staggers the reveal of its motion children as it scrolls
 * into view. Children should be built with `RevealItem`.
 */
export function StaggerGroup({
  as: Component = "div",
  stagger = 0.12,
  delayChildren = 0,
  viewport = defaultViewport,
  children,
  ...props
}) {
  const MotionComponent = React.useMemo(
    () => (typeof Component === "string" ? motion[Component] : motion(Component)),
    [Component]
  );

  return (
    <MotionComponent
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren } },
      }}
      {...props}
    >
      {children}
    </MotionComponent>
  );
}

/**
 * A single item inside a `StaggerGroup`. It has no viewport trigger of its
 * own, it inherits the hidden/visible state from its parent's variants.
 */
export function RevealItem({ as: Component = "div", variants = fadeUp, transition, children, ...props }) {
  const MotionComponent = React.useMemo(
    () => (typeof Component === "string" ? motion[Component] : motion(Component)),
    [Component]
  );

  return (
    <MotionComponent variants={variants} transition={{ ...revealTransition, ...transition }} {...props}>
      {children}
    </MotionComponent>
  );
}
